import React, { useEffect, useState } from "react";
import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import edit from "../../images/edit.svg";
import { UpdatePermissionModal } from "./adminComp/updatePermissionsModal";
import "./AdminDashboard.css"; // Import CSS file

function SuperAdminAllSupport({ adminType }) {
  const adminStore = useSelector((state) => state.adminReducer);

  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currentSupport, setCurrentSupport] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState({});
  const [permissionModal, setPermissionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Trade balance state
  const [portfolioMap, setPortfolioMap] = useState({});
  const [balanceTarget, setBalanceTarget] = useState(null); // { _id, name, email }
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [balanceMsg, setBalanceMsg] = useState("");
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  const getSupport = async () => {
    try {
      setLoader(true);
      let withstats = true;

      const res = await api.getSupportAdmin(withstats);
      if (res?.success) {
        setData(res.support);
        let currentS = res.support.find(
          (d) => d?._id == adminStore?.admin?.admin?._id,
        );
        setCurrentSupport(currentS);
        fetchPortfolios(res.support);
      }
      setLoader(false);
    } catch (error) {
      console.log(error?.message);
      setLoader(false);
    }
  };

  const fetchPortfolios = async (supportList) => {
    try {
      const results = await Promise.all(
        supportList.map(async (s) => {
          try {
            const res = await api.getSupportPortfolio(s._id);
            return { adminId: s._id, portfolio: res?.portfolio || null };
          } catch {
            return { adminId: s._id, portfolio: null };
          }
        }),
      );
      const map = {};
      results.forEach((r) => { map[r.adminId] = r.portfolio; });
      setPortfolioMap(map);
    } catch (e) {
      console.log(e?.message);
    }
  };

  const openBalanceModal = (item) => {
    setBalanceTarget(item);
    const existing = portfolioMap[item._id];
    setBalanceAmount(existing ? String(existing.allocatedBalance) : "");
    setBalanceMsg("");
    setShowBalanceModal(true);
  };

  const handleSaveBalance = async () => {
    if (!balanceTarget || !balanceAmount) return;
    setBalanceSaving(true);
    setBalanceMsg("");
    try {
      const res = await api.assignSupportBalance({
        adminId: balanceTarget._id,
        allocatedBalance: parseFloat(balanceAmount),
      });
      if (res?.success) {
        setPortfolioMap((prev) => ({
          ...prev,
          [balanceTarget._id]: res.portfolio,
        }));
        setBalanceMsg("Saved successfully.");
        setTimeout(() => setShowBalanceModal(false), 800);
      } else {
        setBalanceMsg(res?.message || "Failed.");
      }
    } catch (e) {
      setBalanceMsg(e?.response?.data?.message || "Error saving balance.");
    }
    setBalanceSaving(false);
  };

  useEffect(() => {
    getSupport();
  }, []);

  const openPermissionModal = (item) => {
    setSelectedAdmin(item);
    setPermissionModal(true);
  };
  const handleClose = () => setPermissionModal(false);

  // Calculate statistics
  const calculateStats = () => {
    const THREE_MINUTES = 3 * 60 * 1000;
    const onlineCount = data.filter((item) => {
      if (!item?.lastActiveAt) return false;
      return Date.now() - new Date(item.lastActiveAt).getTime() < THREE_MINUTES;
    }).length;

    const totalProcessing = data.reduce(
      (sum, item) => sum + (item?.processedCount || 0),
      0,
    );
    const totalClosed = data.reduce(
      (sum, item) => sum + (item?.closedCount || 0),
      0,
    );
    const avgTickets =
      data.length > 0
        ? Math.round((totalProcessing + totalClosed) / data.length)
        : 0;

    return {
      onlineCount,
      totalProcessing,
      totalClosed,
      avgTickets,
    };
  };

  const stats = calculateStats();

  return (
    <>
      <div className="container-fluid admin-dashboard support-admin-page">
        {/* Stats Overview Cards */}
        <div className="row stats-overview mb-4">
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="stat-card primary-gradient">
              <div className="stat-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{data?.length || 0}</div>
                <div className="stat-label1">Total Admins</div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="stat-card success-gradient">
              <div className="stat-icon">
                <i className="fas fa-user-check"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.onlineCount}</div>
                <div className="stat-label1">Online Now</div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="stat-card warning-gradient">
              <div className="stat-icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalProcessing}</div>
                <div className="stat-label1">Processing</div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="stat-card info-gradient">
              <div className="stat-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalClosed}</div>
                <div className="stat-label1">Closed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header with Actions */}
        <div className="row page-header mb-4">
          <div className="col-12">
            <div className="page-header-card">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1 className="page-title">Support Team Management</h1>
                  <p className="page-subtitle">
                    Manage and monitor your support team members
                  </p>
                </div>
                <div className="header-actions">
                  <button
                    className="btn-action refresh-btn"
                    onClick={getSupport}
                    title="Refresh"
                  >
                    <i className="fas fa-sync-alt"></i> Refresh
                  </button>
                  {/* <button className="btn-action export-btn">
                    <i className="fas fa-download"></i> Export
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="row search-filter-section mb-4">
          <div className="col-md-8">
            <div className="search-box-wrapper">
              <i className="fas fa-search search-box-icon"></i>
              <input
                type="text"
                className="search-box-input"
                placeholder="Search admins by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="filter-actions">
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="online">Online Only</option>
                <option value="offline">Offline Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Table Section */}
        <div className="row">
          <div className="col">
            <div className="table-header-section">
              <div className="table-title-group">
                <h3 className="table-title">Support Team Members</h3>
                <span className="table-subtitle">
                  {data?.length || 0} team members
                </span>
              </div>
            </div>

            <TableWithStats
              data={data}
              openPermissionModal={openPermissionModal}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              portfolioMap={portfolioMap}
              openBalanceModal={openBalanceModal}
            />
          </div>
        </div>
      </div>

      {loader && <Loader />}
      <UpdatePermissionModal
        getData={getSupport}
        show={permissionModal}
        handleClose={handleClose}
        selectedData={selectedAdmin}
      />

      {/* Assign / Edit Balance Modal */}
      <Modal show={showBalanceModal} onHide={() => setShowBalanceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            Trade Balance — {balanceTarget?.name || balanceTarget?.email}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {portfolioMap[balanceTarget?._id] && (
            <div className="mb-3" style={{ fontSize: 13, color: "#555" }}>
              <div>Allocated: <strong>${portfolioMap[balanceTarget._id].allocatedBalance} USDT</strong></div>
              <div>Current: <strong>${portfolioMap[balanceTarget._id].currentBalance?.toFixed(2)} USDT</strong></div>
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontSize: 13 }}>New Allocated Balance (USDT)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="e.g. 1000"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
            />
            <Form.Text className="text-muted" style={{ fontSize: 11 }}>
              Current balance adjusts by the delta from the previous allocation.
            </Form.Text>
          </Form.Group>
          {balanceMsg && (
            <div className="mt-2" style={{ fontSize: 12, color: "#4caf50" }}>
              {balanceMsg}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowBalanceModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSaveBalance}
            disabled={balanceSaving || !balanceAmount}
          >
            {balanceSaving ? <i className="fa fa-spinner fa-pulse" /> : "Save Balance"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const TableWithStats = ({
  data,
  openPermissionModal,
  searchTerm,
  statusFilter,
  portfolioMap,
  openBalanceModal,
}) => {
  const THREE_MINUTES = 3 * 60 * 1000;

  const isOnline = (lastActiveAt) => {
    if (lastActiveAt) {
      return Date.now() - new Date(lastActiveAt).getTime() < THREE_MINUTES;
    } else {
      return false;
    }
  };

  const formatDate = (date) => {
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  // Filter data based on search and status
  const filteredData = data.filter((item) => {
    const matchesSearch =
      searchTerm === "" ||
      item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "online" && isOnline(item?.lastActiveAt)) ||
      (statusFilter === "offline" && !isOnline(item?.lastActiveAt));

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="card modern-table-card">
      <div className="table-responsive">
        <table className="table modern-table">
          <thead className="table-header">
            <tr>
              <th className="table-head-cell">
                <div className="table-cell-content">Name</div>
              </th>
              <th className="table-head-cell">
                <div className="table-cell-content">Email</div>
              </th>
              <th className="table-head-cell">
                <div className="table-cell-content">Permissions</div>
              </th>
              <th className="table-head-cell text-center">
                <div className="table-cell-content">Processing</div>
              </th>
              <th className="table-head-cell text-center">
                <div className="table-cell-content">Closed</div>
              </th>
              <th className="table-head-cell">
                <div className="table-cell-content">Last Active At</div>
              </th>
              <th className="table-head-cell text-center">
                <div className="table-cell-content">Status</div>
              </th>
              <th className="table-head-cell text-center">
                <div className="table-cell-content">Trade Balance</div>
              </th>
            </tr>
          </thead>
          <tbody className="table-body">
            {filteredData.map((item, key) => {
              const online = isOnline(item?.lastActiveAt);
              return (
                <tr key={key} className="table-row">
                  <td className="table-cell">
                    <div className="user-info-wrapper">
                      <div className="user-avatar-wrapper">
                        <div className="user-avatar">
                          {item?.name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                      </div>
                      <div className="user-details-wrapper">
                        <div className="user-name">{item?.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="email-wrapper">{item?.email}</div>
                  </td>
                  <td className="table-cell">
                    <div className="permissions-wrapper">
                      <div className="permissions-content">
                        {item?.permissions ? (
                          <>
                            {item?.permissions.length > 0 ? (
                              <div className="permissions-badges">
                                {item?.permissions.map((perm, index) => (
                                  <span
                                    key={index}
                                    className="permission-badge"
                                  >
                                    {perm}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="no-permissions">-</span>
                            )}
                          </>
                        ) : (
                          <span className="no-permissions">-</span>
                        )}
                      </div>
                      <button
                        className="edit-permissions-btn"
                        onClick={() => openPermissionModal(item)}
                        title="Edit Permissions"
                      >
                        <img src={edit} alt="edit" className="edit-icon" />
                        <span className="edit-text">Edit</span>
                      </button>
                    </div>
                  </td>
                  <td className="table-cell text-center">
                    <div className="ticket-count-badge processing-badge">
                      {item?.processedCount || 0}
                    </div>
                  </td>
                  <td className="table-cell text-center">
                    <div className="ticket-count-badge closed-badge">
                      {item?.closedCount || 0}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="last-active-wrapper">
                      {item?.lastActiveAt
                        ? formatDate(item?.lastActiveAt)
                        : "-"}
                    </div>
                  </td>
                  <td className="table-cell text-center">
                    <div
                      className={`status-indicator ${
                        online ? "online-status" : "offline-status"
                      }`}
                    >
                      <span className="status-dot"></span>
                      <span className="status-text">
                        {online ? "Online" : "Offline"}
                      </span>
                    </div>
                  </td>
                  <td className="table-cell text-center">
                    {portfolioMap[item._id] ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                          <div style={{ color: "#888", fontSize: 11 }}>Allocated</div>
                          <div style={{ fontWeight: 600 }}>${portfolioMap[item._id].allocatedBalance}</div>
                          <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>Current</div>
                          <div style={{ fontWeight: 600, color: portfolioMap[item._id].currentBalance >= 0 ? "#4caf50" : "#f44336" }}>
                            ${portfolioMap[item._id].currentBalance?.toFixed(2)}
                          </div>
                        </div>
                        <button
                          title="Edit Balance"
                          onClick={() => openBalanceModal(item)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
                        >
                          <img src={edit} alt="edit" style={{ width: 14, opacity: 0.7 }} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        onClick={() => openBalanceModal(item)}
                      >
                        Assign Balance
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredData.length === 0 && (
        <div className="empty-table-state">
          <div className="empty-state-content">
            <i className="fas fa-users empty-state-icon"></i>
            <h4 className="empty-state-title">No support admins found</h4>
            <p className="empty-state-description">
              {data.length === 0
                ? "No support admins have been added yet."
                : "No admins match your search criteria."}
            </p>
          </div>
        </div>
      )}

      {filteredData.length > 0 && (
        <div className="table-footer">
          <div className="table-info">
            Showing {filteredData.length} of {data.length} support admins
          </div>
          <div className="table-pagination">
            <Stack spacing={2}>
              <Pagination
                count={Math.ceil(filteredData.length / 10)}
                shape="rounded"
                size="small"
              />
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAllSupport;
